import { useState, useEffect } from 'react';
import { recordatoriosAPI, pushAPI, usersAPI, authAPI, configAPI } from '../api/client';
import { useTranslation } from 'react-i18next';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) output[i] = rawData.charCodeAt(i);
  return output;
}

const LANG_KEY = 'colmena_lang';

export default function Ajustes() {
  const { t, i18n } = useTranslation();
  const [recs, setRecs] = useState([]);
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState('');

  const [emailEnabled, setEmailEnabled] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushMsg, setPushMsg] = useState('');
  const [pushIsError, setPushIsError] = useState(false);

  const [perfilClimatico, setPerfilClimatico] = useState('invernada');

  useEffect(() => {
    configAPI.get()
      .then(r => {
        const enabled = r.data.email_enabled === true;
        setEmailEnabled(enabled);
        setPushEnabled(r.data.push_enabled === true);
        setConfigLoaded(true);
        if (enabled) recordatoriosAPI.list().then(res => setRecs(res.data)).catch(() => {});
      })
      .catch(() => { setEmailEnabled(false); setPushEnabled(false); });
    authAPI.me().then(r => {
      if (r.data?.perfil_climatico) setPerfilClimatico(r.data.perfil_climatico);
      const serverLang = r.data?.language;
      const localLang = localStorage.getItem(LANG_KEY);
      if (localLang && serverLang && localLang !== serverLang) {
        usersAPI.updateLanguage(localLang).catch(() => {});
      } else if (serverLang && !localLang) {
        i18n.changeLanguage(serverLang);
        try { localStorage.setItem(LANG_KEY, serverLang); } catch {}
      }
    }).catch(() => {});

    const supported = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
    setPushSupported(supported);
    if (supported) {
      setPushPermission(Notification.permission);
      navigator.serviceWorker.ready.then(reg =>
        reg.pushManager.getSubscription()
      ).then(sub => {
        setPushSubscribed(!!sub);
      }).catch(() => {});
    }
  }, []);

  const toggle = async (rec) => {
    await recordatoriosAPI.update(rec.id, { activo: !rec.activo });
    setRecs(prev => prev.map(r => r.id === rec.id ? { ...r, activo: !r.activo } : r));
  };

  const testEmail = async () => {
    setTesting(true); setTestMsg('');
    try {
      const r = await recordatoriosAPI.testEmail();
      setTestMsg(r.data.enviado
        ? t('ajustes.testEmailSent', { email: r.data.email })
        : t('ajustes.testEmailFail'));
    } finally { setTesting(false); }
  };

  const activarPush = async () => {
    setPushLoading(true); setPushMsg(''); setPushIsError(false);
    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      if (permission !== 'granted') {
        setPushMsg(t('ajustes.pushPermDenied')); setPushIsError(true);
        return;
      }
      const { data } = await pushAPI.vapidKey();
      if (!data.public_key) {
        setPushMsg(t('ajustes.pushNotConfigured')); setPushIsError(true);
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      const subJson = sub.toJSON();
      await pushAPI.subscribe({
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      });
      setPushSubscribed(true);
      setPushMsg(t('ajustes.pushActivated'));
    } catch (ex) {
      setPushMsg(t('ajustes.pushActivateError') + (ex.message || ex)); setPushIsError(true);
    } finally {
      setPushLoading(false);
    }
  };

  const desactivarPush = async () => {
    setPushLoading(true); setPushMsg(''); setPushIsError(false);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
      await pushAPI.unsubscribe();
      setPushSubscribed(false);
      setPushMsg(t('ajustes.pushDeactivated'));
    } catch (ex) {
      setPushMsg(t('ajustes.pushDeactivateError') + (ex.message || ex)); setPushIsError(true);
    } finally {
      setPushLoading(false);
    }
  };

  const changePerfil = (perfil) => {
    setPerfilClimatico(perfil);
    usersAPI.updatePerfilClimatico(perfil).catch(() => {});
  };

  const changeLang = (lng) => {
    i18n.changeLanguage(lng);
    try { localStorage.setItem(LANG_KEY, lng); } catch {}
    usersAPI.updateLanguage(lng).catch(() => {});
  };

  const tipoLabel = (tipo) => ({
    diario: t('ajustes.recDaily'),
    semanal: t('ajustes.recWeekly'),
    alerta_roja: t('ajustes.recAlert'),
  }[tipo] || tipo);

  const tipoDesc = (rec) => {
    if (rec.tipo === 'semanal') return t('ajustes.recWeeklyDesc', { day: t(`ajustes.days.${rec.dia_semana || 0}`), hour: rec.hora });
    if (rec.tipo === 'diario') return t('ajustes.recDailyDesc', { hour: rec.hora });
    return t('ajustes.recAlertDesc');
  };

  const currentLang = i18n.language?.slice(0, 2) || 'es';

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 style={S.title}>{t('ajustes.title')}</h1>
        <p style={S.sub}>{t('ajustes.subtitle')}</p>
      </div>

      <section style={S.section}>
        <h2 style={S.sectionTitle}>{t('ajustes.langTitle')}</h2>
        <p style={S.sectionDesc}>{t('ajustes.langLabel')}</p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            { code: 'es', label: t('ajustes.langEs') },
            { code: 'en', label: t('ajustes.langEn') },
            { code: 'de', label: t('ajustes.langDe') },
          ].map(lng => (
            <button
              key={lng.code}
              onClick={() => changeLang(lng.code)}
              style={{
                padding: '9px 20px',
                borderRadius: 10,
                border: '1.5px solid',
                borderColor: currentLang === lng.code ? '#E8922A' : '#FAE0B0',
                background: currentLang === lng.code ? '#FDF0DC' : 'white',
                color: currentLang === lng.code ? '#C97820' : '#5C3D2E',
                fontWeight: currentLang === lng.code ? 600 : 400,
                cursor: 'pointer',
                fontSize: 14,
                fontFamily: "'DM Sans', sans-serif",
                transition: 'all 0.15s',
              }}>
              {lng.label}
            </button>
          ))}
        </div>
      </section>

      <section style={S.section}>
        <h2 style={S.sectionTitle}>{t('ajustes.perfilTitle')}</h2>
        <p style={S.sectionDesc}>{t('ajustes.perfilLabel')}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { code: 'invernada', label: t('ajustes.perfilInvernada'), desc: t('ajustes.perfilInvernadaDesc') },
            { code: 'sin_invernada', label: t('ajustes.perfilSinInvernada'), desc: t('ajustes.perfilSinInvernadaDesc') },
            { code: 'invierno_suave', label: t('ajustes.perfilInviernoSuave'), desc: t('ajustes.perfilInviernoSuaveDesc') },
          ].map(p => {
            const active = perfilClimatico === p.code;
            return (
              <button
                key={p.code}
                onClick={() => changePerfil(p.code)}
                style={{
                  textAlign: 'left',
                  padding: '12px 16px',
                  borderRadius: 12,
                  border: '1.5px solid',
                  borderColor: active ? '#E8922A' : '#FAE0B0',
                  background: active ? '#FDF0DC' : 'white',
                  cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                  transition: 'all 0.15s',
                }}>
                <div style={{ fontWeight: active ? 600 : 500, fontSize: 15, color: active ? '#C97820' : '#5C3D2E' }}>{p.label}</div>
                <div style={{ fontSize: 13, color: '#8B6355', marginTop: 2 }}>{p.desc}</div>
              </button>
            );
          })}
        </div>
      </section>

      {configLoaded && emailEnabled === false && pushEnabled === false && (
        <section style={{ ...S.section, borderColor: 'rgba(100,120,180,0.18)', background: '#F7F8FC' }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 22, lineHeight: 1 }}>ℹ️</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: 15, color: '#3A4A6B', marginBottom: 4 }}>
                {t('ajustes.noNotificationsTitle')}
              </div>
              <p style={{ fontSize: 13, color: '#5A6A8B', lineHeight: 1.6, margin: 0 }}>
                {t('ajustes.noNotificationsMsg')}
              </p>
            </div>
          </div>
        </section>
      )}

      {pushEnabled === true && <section style={S.section}>
        <h2 style={S.sectionTitle}>{t('ajustes.pushTitle')}</h2>
        <p style={S.sectionDesc}>{t('ajustes.pushDesc')}</p>

        {!pushSupported ? (
          <div style={S.infoBox}>
            <p style={{ fontSize: 13, color: '#8B6355' }}>{t('ajustes.pushNotSupported')}</p>
          </div>
        ) : pushPermission === 'denied' ? (
          <div style={{ ...S.infoBox, borderColor: '#FDECEA', background: '#FEF8F8' }}>
            <p style={{ fontSize: 13, color: '#C0392B' }}>{t('ajustes.pushBlocked')}</p>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, padding: '14px 16px', background: '#FEFCF8', borderRadius: 12, border: '1px solid #FAE0B0' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: 15 }}>
                  {pushSubscribed ? t('ajustes.pushOn') : t('ajustes.pushOff')}
                </div>
                <div style={{ fontSize: 13, color: '#8B6355', marginTop: 2 }}>
                  {pushSubscribed ? t('ajustes.pushOnDesc') : t('ajustes.pushOffDesc')}
                </div>
              </div>
              {pushSubscribed ? (
                <button className="btn btn-ghost btn-sm" onClick={desactivarPush} disabled={pushLoading}>
                  {pushLoading ? '...' : t('ajustes.pushDeactivate')}
                </button>
              ) : (
                <button className="btn btn-primary btn-sm" onClick={activarPush} disabled={pushLoading}>
                  {pushLoading ? '...' : t('ajustes.pushActivate')}
                </button>
              )}
            </div>
            {pushMsg && (
              <p style={{ fontSize: 13, color: pushIsError ? '#C0392B' : '#4A7C59', marginTop: 4 }}>
                {pushMsg}
              </p>
            )}
          </div>
        )}
      </section>}

      {emailEnabled === true && (
        <section style={S.section}>
          <h2 style={S.sectionTitle}>{t('ajustes.emailTitle')}</h2>
          <p style={S.sectionDesc}>{t('ajustes.emailDesc')}</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            {recs.map(rec => (
              <div key={rec.id} style={S.recCard}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 15 }}>{tipoLabel(rec.tipo)}</div>
                  <div style={{ fontSize: 13, color: '#8B6355', marginTop: 2 }}>{tipoDesc(rec)}</div>
                </div>
                <label style={S.toggle}>
                  <input type="checkbox" checked={rec.activo} onChange={() => toggle(rec)}
                    style={{ display: 'none' }} />
                  <div style={{ ...S.toggleTrack, background: rec.activo ? '#E8922A' : '#D0C8C0' }}>
                    <div style={{ ...S.toggleThumb, transform: rec.activo ? 'translateX(20px)' : 'translateX(2px)' }} />
                  </div>
                </label>
              </div>
            ))}
          </div>

          <button onClick={testEmail} disabled={testing} className="btn btn-ghost">
            {testing ? t('common.sending') : t('ajustes.testBtn')}
          </button>
          {testMsg && <p style={{ fontSize: 13, color: '#4A7C59', marginTop: 8 }}>{testMsg}</p>}
        </section>
      )}
    </div>
  );
}

const S = {
  title: { fontSize: 26, fontFamily: "'DM Sans', sans-serif" },
  sub: { color: '#8B6355', fontSize: 14, marginTop: 4 },
  section: { background: 'white', borderRadius: 16, padding: '20px 24px', marginBottom: 20, boxShadow: '0 2px 12px rgba(44,26,14,0.08)', border: '1px solid rgba(232,146,42,0.12)' },
  sectionTitle: { fontSize: 17, fontWeight: 600, marginBottom: 8, fontFamily: "'DM Sans', sans-serif" },
  sectionDesc: { fontSize: 13, color: '#8B6355', marginBottom: 16, lineHeight: 1.6 },
  recCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: '#FEFCF8', borderRadius: 12, border: '1px solid #FAE0B0' },
  toggle: { cursor: 'pointer' },
  toggleTrack: { width: 44, height: 24, borderRadius: 12, position: 'relative', transition: 'background 0.2s' },
  toggleThumb: { position: 'absolute', top: 2, width: 20, height: 20, borderRadius: '50%', background: 'white', transition: 'transform 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' },
  infoBox: { background: '#FEFCF8', borderRadius: 12, padding: '16px 20px', border: '1px solid #FAE0B0' },
};
